"use client";

import { useState, useTransition } from "react";
import Link from "next/link";
import { addLineItem, openCartDrawer } from "../../lib/cart-store";
import type { CatalogLite } from "../../lib/types";
import { wixSized, wixSrcSet } from "../../lib/media";

/**
 * Catalog tile. The hover action is a single button whose label matches what it
 * actually does:
 *
 *   - product with options -> "Select Size" (the option's own name), which asks
 *     ProductGrid to open the inline quick-add panel under this row.
 *   - product without options -> "Quick Add", which adds straight to the cart
 *     and slides the cart drawer open.
 *
 * The label used to branch on `manageVariants` while the behaviour branched on
 * `manageVariants || hasOptions`. manageVariants is false for all 110 products,
 * so the 16 that carry a Size option rendered "Quick Add" and then silently did
 * something else — the label half of the size fix never landed.
 */
// 3:4 card crops. Wix resizes server-side and serves avif/webp; the raw main
// image was the full upload (a 7.7 MB PNG for one tee), on every grid tile.
const CARD_WIDTHS = [270, 360, 540, 720, 960];

export default function ProductCard({
  product,
  isOpen,
  onToggleQuickAdd,
  priority = false,
  sizes = "(min-width: 768px) 25vw, 45vw",
}: {
  product: CatalogLite;
  isOpen: boolean;
  onToggleQuickAdd: () => void;
  /** Above-the-fold card: fetch eagerly at high priority (the mobile LCP). */
  priority?: boolean;
  /** Rendered width hint. Grids are 2 columns on phones (~45vw); carousels pass a wider one. */
  sizes?: string;
}) {
  const [pending, startTransition] = useTransition();
  const [added, setAdded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const needsChoice = product.manageVariants || Boolean(product.hasOptions);
  const optionName = product.options?.[0]?.name ?? "Size";

  function onAction(e: React.MouseEvent) {
    e.preventDefault();
    if (!product.inStock || pending) return;
    if (needsChoice) {
      onToggleQuickAdd();
      return;
    }
    setError(null);
    startTransition(async () => {
      try {
        await addLineItem({ productId: product.id, quantity: 1 });
        setAdded(true);
        openCartDrawer();
        setTimeout(() => setAdded(false), 1800);
      } catch (err) {
        console.error("[quick-add] failed", err);
        setError("Could not add to cart — please try again.");
      }
    });
  }

  const actionLabel = !product.inStock
    ? "Sold Out"
    : needsChoice
      ? isOpen
        ? "Close"
        : `Select ${optionName}`
      : added
        ? "Added ✓"
        : pending
          ? "Adding…"
          : "Quick Add";

  return (
    <div className="group relative">
      <div className="relative aspect-[3/4] overflow-hidden bg-panel">
        {/* The link and the quick-add button are siblings: a button nested in
            an anchor is invalid, and image-less cards got the link name
            "Select Size". */}
        <Link
          href={`/shop/${product.slug}`}
          aria-label={product.name}
          className="absolute inset-0 block focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-ink"
        >
          {product.image ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={wixSized(product.image, 540, 720)}
              srcSet={wixSrcSet(product.image, CARD_WIDTHS, 4 / 3)}
              sizes={sizes}
              alt=""
              loading={priority ? "eager" : "lazy"}
              fetchPriority={priority ? "high" : undefined}
              decoding="async"
              className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.03]"
            />
          ) : (
            <span className="absolute inset-0 flex items-center justify-center text-mist">
              <svg
                width="40"
                height="40"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.2"
                aria-hidden="true"
                role="presentation"
              >
                <rect x="3" y="3" width="18" height="18" rx="1.5" />
                <circle cx="8.5" cy="8.5" r="1.6" />
                <path d="M21 15l-5-5L5 21" />
              </svg>
            </span>
          )}
        </Link>

        {!product.inStock && (
          <span className="pointer-events-none absolute left-2 top-2 bg-ink/90 px-2 py-1 text-[10px] uppercase tracking-[0.15em] text-paper">
            Sold Out
          </span>
        )}

        <div
          className={`absolute inset-x-0 bottom-0 transition-all duration-300 group-focus-within:translate-y-0 group-focus-within:opacity-100 group-hover:translate-y-0 group-hover:opacity-100 [@media(hover:none)]:translate-y-0 [@media(hover:none)]:opacity-100 ${
            isOpen ? "translate-y-0 opacity-100" : "translate-y-2 opacity-0"
          }`}
        >
          <button
            type="button"
            onClick={onAction}
            disabled={!product.inStock}
            aria-disabled={pending || undefined}
            aria-expanded={needsChoice ? isOpen : undefined}
            aria-label={`${actionLabel}: ${product.name}`}
            data-quick-add-trigger={product.id}
            className="w-full bg-ink/95 py-2.5 text-[10px] uppercase tracking-[0.15em] text-paper backdrop-blur transition hover:bg-gold-deep disabled:opacity-60 aria-disabled:opacity-60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-paper"
          >
            {actionLabel}
          </button>
        </div>
      </div>

      <div className="mt-3 flex items-start justify-between gap-3">
        <p className="min-w-0 text-[12px] uppercase tracking-wide [overflow-wrap:anywhere]">{product.name}</p>
        <p className="flex shrink-0 items-center gap-2 text-[12px] text-ink/70">
          {product.compareAt && <span className="text-ink/40 line-through">{product.compareAt}</span>}
          {product.price}
        </p>
      </div>
      {error && (
        <p role="alert" className="mt-1 text-[12px] text-red-700">
          {error}
        </p>
      )}
    </div>
  );
}
